import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Preloader } from '@common-fe/common-fe';

import ROUTES from '@/common/routes';
import { WarnModal } from '@/components/elements';
import { useHistory, useRedirectToHome } from '@/modules/core/hooks';
import { FeatureFlags } from '@/modules/featureFlags/featureFlags.constants';
import { useFeatureFlag } from '@/modules/featureFlags/hooks/useFeatureFlag';
import { useUpdateRenewalSetup } from '@/modules/Renewals/RenewalSetup/hooks';

import { RenewalStatus } from '../renewals.types';

import NextSteps from './RenewalSetupModal/NextSteps';
import RenewalSetupModal from './RenewalSetupModal/RenewalSetupModal';
import { resetAllStores } from './stores/storesResetter';
import useHealthPlansStore from './stores/useHealthPlans.store';
import CopaysStep from './CopaysStep';
import HealthPlansStep from './HealthPlansStep';
import { useCurrentRenewalSetup } from './hooks';
import NavigationBackButton from './NavigationBackButton';
import PlanYearPlansStep from './PlanYearPlansStep';
import ProgressBar from './ProgressBar';
import { RenewalPlanOptionValues, RenewalSetupStep } from './RenewalSetup.types';
import RenewalSetupPrompt from './RenewalSetupPrompt';
import RenewalSetupWrapper from './RenewalSetupWrapper';
import { useRenewalSetupStore } from './stores';

export const stepsWithHealthPlans: RenewalSetupStep[] = [
  RenewalSetupStep.PlansPlanYear,
  RenewalSetupStep.HealthPlans,
  RenewalSetupStep.Copays,
  RenewalSetupStep.RenewalPopup,
];

// Keep backward-compatible export for NavigationBackButton and other consumers
export const steps = stepsWithHealthPlans;

export const stepsWithoutHealthPlans: RenewalSetupStep[] = [
  RenewalSetupStep.PlansPlanYear,
  RenewalSetupStep.Copays,
  RenewalSetupStep.RenewalPopup,
];

export const stepsCanceledMode: RenewalSetupStep[] = [RenewalSetupStep.PlansPlanYear];

export const RENEWAL_SETUP_CONTENT_WIDTH = '648px';

const RenewalSetup: React.FC = () => {
  const history = useHistory();
  const isHealthPlanRenewalEnabled = useFeatureFlag(FeatureFlags.HEALTH_PLAN_RENEWAL);
  const healthPlansFromStore = useHealthPlansStore((state) => state.state.healthPlans);
  const steps = useMemo(
    () =>
      isHealthPlanRenewalEnabled && (healthPlansFromStore?.length || 0) > 0
        ? stepsWithHealthPlans
        : stepsWithoutHealthPlans,
    [isHealthPlanRenewalEnabled, healthPlansFromStore?.length]
  );
  const plansRenewSelectedOption = useRenewalSetupStore((state) => state.plansPlanYear.selectedOption);
  const {
    renewalSetupData,
    serviceCategories,
    isLoading: isRenewalSetupLoading,
    isError: isRenewalSetupError,
  } = useCurrentRenewalSetup();
  const [isVisibleRenewalPopup, setVisibleRenewalPopup] = useState(false);
  const [isSuccessModal, setSuccessModal] = useState(false);
  const [isPromptDisabled, setPromptDisabled] = useState(false);
  const [errorModal, setErrorModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(steps[0]);

  const handleSuccess = useCallback(() => {
    setVisibleRenewalPopup(false);
    setSuccessModal(true);
  }, []);

  const { handleSave, isError, isLoading } = useUpdateRenewalSetup({
    renewalSetupData,
    handleSetPromptDisabled: setPromptDisabled,
    onSuccess: handleSuccess,
  });

  const setStepName = (stepName: RenewalSetupStep) => {
    setCurrentStep(stepName);
  };

  const handleSubmit = useCallback(() => {
    history.push(ROUTES.HOME);
  }, [history]);

  const handleSetVisibleRenewalPopup = useCallback((value: boolean) => {
    setVisibleRenewalPopup(value);
  }, []);

  const successModalTitle = useMemo(() => {
    if (plansRenewSelectedOption === RenewalPlanOptionValues.ACTIVE)
      return 'Your plan has been renewed for the upcoming year';
    if (plansRenewSelectedOption === RenewalPlanOptionValues.REQUESTED)
      return `Your renewal request has been sent to ${renewalSetupData?.partnerName}.`;
    return 'Your request to not renew has been sent';
  }, [plansRenewSelectedOption, renewalSetupData?.partnerName]);

  const MainWizardComponent = useMemo(() => {
    if (currentStep === RenewalSetupStep.Copays) {
      return (
        <CopaysStep
          steps={steps}
          renewalSetupData={renewalSetupData}
          serviceCategories={serviceCategories}
          handleSetVisibleRenewPopup={handleSetVisibleRenewalPopup}
        />
      );
    }
    if (currentStep === RenewalSetupStep.HealthPlans) {
      return (
        <HealthPlansStep
          steps={steps}
          currentStep={currentStep}
          renewalSetupData={renewalSetupData}
          handleStepName={setStepName}
        />
      );
    }
    return (
      <PlanYearPlansStep
        steps={steps}
        currentStep={currentStep}
        renewalSetupData={renewalSetupData}
        handleStepName={setStepName}
        handleSetVisibleRenewPopup={handleSetVisibleRenewalPopup}
      />
    );
  }, [currentStep, handleSetVisibleRenewalPopup, renewalSetupData, serviceCategories, steps]);

  useEffect(() => {
    if (isError) setErrorModal(true);
  }, [isError]);

  useEffect(
    () => () => {
      resetAllStores();
    },
    []
  );

  useRedirectToHome(
    isRenewalSetupError || (!!renewalSetupData?.status && renewalSetupData?.status !== RenewalStatus.NEW)
  );

  if (isRenewalSetupLoading) {
    return <Preloader testId="preloader" />;
  }

  return (
    <RenewalSetupWrapper renewalSetupData={renewalSetupData}>
      <RenewalSetupPrompt isPromptDisabled={isPromptDisabled} />
      <Box width={RENEWAL_SETUP_CONTENT_WIDTH} margin={{ right: 'l' }}>
        {currentStep !== steps[0] && (
          <NavigationBackButton currentStep={currentStep} steps={steps} handleStepName={setStepName} />
        )}
        {MainWizardComponent}
      </Box>
      <Box width="304px">
        <ProgressBar
          stepName={currentStep}
          handleStepName={setStepName}
          renewalSetupData={renewalSetupData}
        />
      </Box>
      <RenewalSetupModal
        isVisible={isVisibleRenewalPopup}
        isLoading={isLoading}
        onSave={handleSave}
        steps={steps}
        renewalSetupData={renewalSetupData}
        onSetVisible={handleSetVisibleRenewalPopup}
        handleStepName={setStepName}
      />
      <WarnModal
        testId="renewal-setup_success_modal"
        visible={isSuccessModal}
        onSetVisible={handleSubmit}
        header={successModalTitle}
        helptext={`If you want to make changes, please contact ${renewalSetupData?.partnerName}.`}
        isSuccessIcon
        paddingless
        submitButtonText="Close"
        onSubmit={handleSubmit}
      >
        <NextSteps renewalSetupData={renewalSetupData} />
      </WarnModal>
      <WarnModal
        testId="renewal-wizard-error-modal"
        visible={errorModal}
        onSetVisible={setErrorModal}
        header="The server encountered an error processing the request"
        helptext={`It doesn't appear to have affected your data, but we cannot renew your plan with the latest records.
        Our technical staff have been automatically notified and will be looking into this with the utmost urgency.`}
        buttonText="Close"
        onSubmit={handleSave}
        submitButtonText="Try again"
      />
    </RenewalSetupWrapper>
  );
};

export default RenewalSetup;
